import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { RequiredInput } from "@/components/common/required-input";
import { Separator } from "@/components/ui/separator";
import {
  PAYMENT_METHOD_OPTIONS,
  SALE_LINE_ROW_GRID,
  SALE_PAYMENT_ROW_GRID,
} from "@/constants/sales/sales-constants";
import { isoDate, isoTime } from "@/lib/utils";
import {
  saleClientLabel,
  saleTimeSpan,
} from "@/lib/sales/sale-form-utils";
import type { Sale } from "@/types/sales/api-types/sale-types";
import { useIntl } from "react-intl";

interface SaleInvoiceDialogProps {
  sale: Sale;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const methodLabel = (method: string): string =>
  PAYMENT_METHOD_OPTIONS.find((option) => option.value === method)?.label ??
  method;

export function SaleInvoiceDialog({
  sale,
  open,
  onOpenChange,
}: SaleInvoiceDialogProps) {
  const intl = useIntl();
  const span = saleTimeSpan(sale.services);
  const timeRange = span ? intl.formatDateTimeRange(isoTime(span.start), isoTime(span.end), { format: "clock" }) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Sale for {saleClientLabel(sale.client_name)}</DialogTitle>
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{intl.formatDate(isoDate(sale.appointment_date), { format: "long" })}</span>
            {timeRange ? <span>{timeRange}</span> : null}
          </div>
        </DialogHeader>
        <Separator />

        <div className="flex flex-col gap-6">
          <div>
            <p className="text-sm font-semibold mb-3">Services</p>
            {sale.services.map((line) => (
              <div key={line.id} className={`${SALE_LINE_ROW_GRID} mb-3`}>
                <Field>
                  <FieldLabel>
                    Service
                    <RequiredInput />
                  </FieldLabel>
                  <Input value={line.service_name ?? ""} disabled />
                </Field>
                <Field>
                  <FieldLabel>
                    Stylist
                    <RequiredInput />
                  </FieldLabel>
                  <Input value={line.staff_name ?? ""} disabled />
                </Field>
                <Field>
                  <FieldLabel>
                    Price
                    <RequiredInput />
                  </FieldLabel>
                  <div className="relative">
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base md:text-sm"
                    >
                      $
                    </span>
                    <Input
                      value={line.price}
                      disabled
                      className="pl-6"
                    />
                  </div>
                </Field>
                <div />
              </div>
            ))}
          </div>

          <Separator />

          <div>
            <p className="text-sm font-semibold mb-3">Products</p>
            {sale.products.map((line) => (
              <div key={line.id} className={`${SALE_LINE_ROW_GRID} mb-3`}>
                <Field>
                  <FieldLabel>
                    Product
                    <RequiredInput />
                  </FieldLabel>
                  <Input value={line.product_name ?? ""} disabled />
                </Field>
                <Field>
                  <FieldLabel>Stylist</FieldLabel>
                  <Input value={line.staff_name ?? ""} disabled />
                </Field>
                <Field>
                  <FieldLabel>
                    Price
                    <RequiredInput />
                  </FieldLabel>
                  <div className="relative">
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base md:text-sm"
                    >
                      $
                    </span>
                    <Input
                      value={line.price}
                      disabled
                      className="pl-6"
                    />
                  </div>
                </Field>
                <div />
              </div>
            ))}
          </div>

          <Separator />

          <div className="flex items-end justify-between gap-4">
            <Field className="max-w-32">
              <FieldLabel>Discount</FieldLabel>
              <div className="relative">
                <Input value={sale.discount_percent} disabled className="pr-8" />
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base md:text-sm"
                >
                  %
                </span>
              </div>
            </Field>
            <p className="text-xl font-bold">
              Total {intl.formatNumber(sale.total_price, { format: "money" })}
            </p>
          </div>

          <Separator />

          <div>
            <p className="text-sm font-semibold mb-3">Payment</p>
            {sale.payments.map((line) => (
              <div key={line.id} className={`${SALE_PAYMENT_ROW_GRID} mb-3`}>
                <Field>
                  <FieldLabel>
                    Method
                    <RequiredInput />
                  </FieldLabel>
                  <Input value={methodLabel(line.method)} disabled />
                </Field>
                <Field>
                  <FieldLabel>
                    Amount
                    <RequiredInput />
                  </FieldLabel>
                  <div className="relative">
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base md:text-sm"
                    >
                      $
                    </span>
                    <Input
                      value={line.amount}
                      disabled
                      className="pl-6"
                    />
                  </div>
                </Field>
                <div />
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button className="w-24" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
